import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, InputLabel, Paper, Stack, Typography, useTheme } from "@mui/material";
import { RangeWithStepOrNumber } from "types";
import { Help } from "components/Help";
import { Slider } from "components/Slider";
import { NumberField } from "components/NumberField";
import getStyles from "./styles";

type Props = {
  name: string;
  label: string;
  tooltip?: string;
  data: RangeWithStepOrNumber;
  optimize: boolean;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (name: string, data: RangeWithStepOrNumber) => void;
  onSelect: (name: string, data: boolean) => void;
};

const StepSlider: FC<Props> = ({ name, label, tooltip, step: initialStep = 1, unit, data, optimize, min, max, onChange, onSelect }) => {
  const [step, setStep] = useState<number>(initialStep || 1);
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  const sliderData = useMemo(() => ({
    value: data.value,
    range: {
      min: data.rangeValue.min,
      max: data.rangeValue.max,
      step: data.rangeValue.step,
    },
  }), [data.rangeValue.max, data.rangeValue.min, data.rangeValue.step, data.value]);

  const handleSelect = useCallback((name: string, optimize: boolean) => {
    onSelect(name, optimize);
  }, [onSelect]);

  const handleCheckboxChange = useCallback(() => {
    handleSelect(name, !optimize);
  }, [handleSelect, name, optimize]);

  const handleChange = useCallback((
    name: string,
    data: {
      value: number;
      range: {
        min: number;
        max: number;
        step: number;
      };
    }
  ) => {
    onChange(name, {
      value: data.value as number,
      rangeValue: {
        min: data.range.min,
        max: data.range.max,
        step: data.range.step,
      },
    });
  }, [onChange]);

  const handleStep = useCallback((name: string, value: number) => {
    const result = {
      ...data,
      rangeValue: {
        ...data.rangeValue,
        step: value,
      },
    };
    setStep(value);
    onChange(name, result);
  }, [data, onChange]);

  return (
    <Paper sx={styles.card} className="RlxStepSlider">
      <Stack sx={styles.controls}>
        <Stack sx={styles.checkbox}>
          <InputLabel htmlFor={`step-slider-${name}`} sx={styles.label}>
            {label} ({unit}){tooltip && <Help text={tooltip} />}
          </InputLabel>
          <FormControlLabel
            id={`step-slider-${name}`}
            control={<Checkbox name={name} checked={optimize} value={optimize} onChange={handleCheckboxChange} />}
            label={t("Range")}
            sx={{}}
          />
        </Stack>

        <Box sx={styles.slider}>
          <Slider
            range={optimize}
            name={name}
            data={sliderData}
            step={step as number}
            min={min}
            max={max}
            onChange={handleChange}
          />
        </Box>
      </Stack>

      <Box sx={{ ...styles.box, backgroundColor: optimize ? "var(--color-primary)" : "transparent" }}>
        {optimize ? (
          <NumberField
            variant="filled"
            name={name}
            value={step}
            label={`${t("in steps of")} (${unit})`}
            step={initialStep as number}
            precision={2}
            onChange={handleStep}
            sx={styles.steps}
          />
        ) : (
          <Stack sx={styles.info}>
            <Typography variant="body2">
              {t("in steps of")} ({unit})
            </Typography>
            <Typography variant="body2" sx={styles.step}>{step}</Typography>
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

StepSlider.displayName = "StepSlider";

export default memo(StepSlider);
